import { useEffect } from "react";

import { useFlagPylonWebChatDisabled } from "@/hooks/use-flag";
import { clearChatIdentityTokenCache, useGetAccount, useGetChatIdentityToken } from "@/queries/account-queries";
import { useOrganization } from "@/queries/organization-queries";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";

const pylonUnauthenticatedUserIdKey = "pylon-unauthenticated-user-id";

export const useBootInAppChat = (delayMs = 3000) => {
    const APP_ID = "efa0bf58-5742-41dd-81e3-ffcb7cccda75";
    const PYLON_WIDGET_URL = `https://widget.usepylon.com/widget/${APP_ID}`;

    const { data: account } = useGetAccount();
    const { data: organization } = useOrganization();
    const { value: disableChat } = useFlagPylonWebChatDisabled();
    const { data: emailHash } = useGetChatIdentityToken(account?.email);

    useEffect(() => {
        const findScript = () => {
            return document.querySelector(`script[src="${PYLON_WIDGET_URL}"]`);
        };

        const removePylon = () => {
            const script = findScript();
            if (script && document.head.contains(script)) {
                try {
                    document.head.removeChild(script);
                } catch (error) {
                    console.error("Failed to remove Pylon script", error);
                }
            }
            // Pylon("hide") immediately closes the chat window.
            // Setting window.pylon to undefined prevents re-initialization.
            if (window.Pylon) {
                window.Pylon("hide");
            }
            window.pylon = undefined;
        };

        if (disableChat) {
            removePylon();
            return;
        }

        if (findScript()) {
            return;
        }

        const timeoutId = setTimeout(() => {
            // Double-check before injecting in case another instance loaded it
            if (findScript()) {
                return;
            }

            const script = document.createElement("script");
            script.setAttribute("type", "text/javascript");
            script.setAttribute("async", "true");
            script.setAttribute("src", PYLON_WIDGET_URL);
            script.onload = () => {
                disableAnimations().catch(console.error);
            };
            script.onerror = (error) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const err = error instanceof ErrorEvent ? error.error : error;
                console.log("Not booting Pylon: failed to inject script", err);
            };
            document.head.insertBefore(script, document.head.firstChild);
        }, delayMs);

        return () => {
            clearTimeout(timeoutId);
            removePylon();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disableChat]);

    // Configure Pylon user context and custom fields
    useEffect(() => {
        if (disableChat) {
            return;
        }

        let email = account?.email;
        if (!email) {
            if (!window.localStorage.getItem(pylonUnauthenticatedUserIdKey)) {
                window.localStorage.setItem(pylonUnauthenticatedUserIdKey, crypto.randomUUID());
            }
            email = `unauthenticated-${window.localStorage.getItem(pylonUnauthenticatedUserIdKey)}@example.com`;
        }

        window.pylon = {
            chat_settings: {
                app_id: APP_ID,
                email: email,
                name: account?.name ?? "Unauthenticated",
                avatar_url: account?.avatarUrl,
                account_external_id: account?.id,
                // Identity verification hash for authenticated users only
                // See: https://docs.usepylon.com/pylon-docs/chat-widget/identity-verification
                ...(account && emailHash ? { email_hash: emailHash } : {}),
            },
        };
        if (window.Pylon) {
            // Fields need to be first configured in Pylon
            const customFields: Record<string, string> = {
                product: "Flex",
            };

            // Add organization tier if available (including UNSPECIFIED which is 0)
            if (organization?.tier !== undefined) {
                // Convert enum to string representation
                const tierName =
                    Object.entries(OrganizationTier).find(([, value]) => value === organization.tier)?.[0] ||
                    "UNSPECIFIED";
                customFields.organization_tier = tierName;
            }

            window.Pylon("setNewIssueCustomFields", customFields);
        }
    }, [account, organization, emailHash, disableChat]);
};

/**
 * Hides the Pylon chat widget (bubble + window) while the calling component
 * is mounted, then restores it on unmount.
 *
 * Uses a CSS data attribute on <body> rather than the Pylon JS API because
 * setting window.pylon (config) re-initialises the widget asynchronously
 * and resets any prior hideChatBubble() calls.
 */
export const useHidePylonChat = () => {
    useEffect(() => {
        document.body.setAttribute("data-pylon-hidden", "true");
        return () => {
            document.body.removeAttribute("data-pylon-hidden");
        };
    }, []);
};

export const useNewInAppChatMessage = () => {
    const { value: disableChat } = useFlagPylonWebChatDisabled();
    return (message: string) => {
        if (disableChat) {
            return;
        }
        if (window.Pylon) {
            window.Pylon("showNewMessage", message);
        }
    };
};

export const useResetPylon = () => {
    return () => {
        window.pylon = undefined;
        window.localStorage.removeItem(pylonUnauthenticatedUserIdKey);
        clearChatIdentityTokenCache();
    };
};

const disableAnimations = async () => {
    try {
        const findAnimations = () => {
            const chatWindow = document.getElementsByClassName("PylonChat-chatWindow")[0];
            if (chatWindow && chatWindow.tagName === "IFRAME") {
                const iframeDoc = (chatWindow as HTMLIFrameElement).contentDocument;
                return iframeDoc?.getElementsByClassName("PylonChat-backgroundBlob--container")[0];
            }
        };

        for (let i = 0; i < 10; i++) {
            const animations = findAnimations();
            if (animations) {
                animations.remove();
                console.log("Animations removed");
                return;
            }
            await new Promise((resolve) => setTimeout(resolve, 1000));
        }
    } catch (error) {
        console.error("Failed to disable animations", error);
    }
};
